"use client";

import { createContext, type ReactNode, use } from "react";

import { type UseDialogReturn, useDialog } from "@/lib/hooks/use-dialog";

import { CreateEmbeddedWalletDialog } from "@/subsystems/access/turnkey/components/create-embedded-wallet-dialog";
import { ResetPasskeyDialog } from "@/subsystems/embedded-wallets/components/passkey/reset-passkey-dialog";
import { SignerEnrollDialog } from "@/subsystems/embedded-wallets/components/passkey/signer-enroll-dialog";
import { InviteSignerDialog } from "@/subsystems/embedded-wallets/components/signers/invite-signer-dialog";
import {
  RemoveSignerDialog,
  type RemoveSignerDialogData,
} from "@/subsystems/embedded-wallets/components/signers/remove-signer-dialog";
import { SmsRecoveryDialog } from "@/subsystems/embedded-wallets/components/sms-recovery/sms-recovery-dialog";
import type { SmsRecoveryDialogData } from "@/subsystems/embedded-wallets/components/sms-recovery/sms-recovery-types";
import {
  type AddBlockchainAddressChooserData,
  AddBlockchainAddressChooserDialog,
} from "@/subsystems/transfer/accounts/components/add-blockchain-address-chooser-dialog";
import {
  BlockchainAddressAddNetworkFormDialog,
  type BlockchainAddressAddNetworkFormDialogData,
} from "@/subsystems/transfer/accounts/components/blockchain-address-add-network-form-dialog";
import { BlockchainAddressesAddFormDialog } from "@/subsystems/transfer/accounts/components/blockchain-addresses-add-form-dialog";
import { BankAccountsAddFormDialog } from "@/subsystems/transfer/bank-accounts/components/bank-accounts-add-form-dialog";

export type DialogsContextState = {
  addBankAccountDialogControls: UseDialogReturn<void>;
  addBlockchainAddressChooserDialogControls: UseDialogReturn<AddBlockchainAddressChooserData>;
  addBlockchainAddressesDialogControls: UseDialogReturn<void>;
  addBlockchainAddressNetworkDialogControls: UseDialogReturn<BlockchainAddressAddNetworkFormDialogData>;
  createEmbeddedWalletDialogControls: UseDialogReturn<void>;
  inviteSignerDialogControls: UseDialogReturn<void>;
  removeSignerDialogControls: UseDialogReturn<RemoveSignerDialogData>;
  resetPasskeyDialogControls: UseDialogReturn<void>;
  signerEnrollDialogControls: UseDialogReturn<void>;
  smsRecoveryDialogControls: UseDialogReturn<SmsRecoveryDialogData>;
};

const DialogsContext = createContext<DialogsContextState>(null!);

export function DialogsContextProvider({ children }: { children: ReactNode }) {
  const addBankAccountDialogControls = useDialog({
    eventProps: {
      elementKey: "add-bank-account",
    },
  });
  const addBlockchainAddressesDialogControls = useDialog({
    eventProps: {
      elementKey: "add-blockchain-addresses",
    },
  });
  const addBlockchainAddressNetworkDialogControls = useDialog<BlockchainAddressAddNetworkFormDialogData>({
    eventProps: {
      elementKey: "add-blockchain-address-network",
    },
  });
  const createEmbeddedWalletDialogControls = useDialog({
    eventProps: {
      elementKey: "embedded-wallet-flow",
    },
  });
  const addBlockchainAddressChooserDialogControls = useDialog<AddBlockchainAddressChooserData>({
    eventProps: {
      elementKey: "add-blockchain-address-chooser",
    },
  });
  const smsRecoveryDialogControls = useDialog<SmsRecoveryDialogData>({
    eventProps: {
      elementKey: "embedded-wallet-sms-recovery",
    },
    getEventProps: (data) => ({ eventDetails: { entry: data?.entry, mode: data?.mode } }),
  });
  const resetPasskeyDialogControls = useDialog({
    eventProps: {
      elementKey: "embedded-wallet-passkey-reset",
    },
  });
  const inviteSignerDialogControls = useDialog({
    eventProps: {
      elementKey: "embedded-wallet-invite-signer",
    },
  });
  const removeSignerDialogControls = useDialog<RemoveSignerDialogData>({
    eventProps: {
      elementKey: "embedded-wallet-remove-signer",
    },
    getEventProps: (data) => ({
      eventDetails: { kind: !data?.signer.enrolled ? "withdraw" : data.signer.isCaller ? "self" : "other" },
    }),
  });
  const signerEnrollDialogControls = useDialog({
    eventProps: {
      elementKey: "embedded-wallet-signer-enroll",
    },
  });
  const dialogControls: DialogsContextState = {
    addBankAccountDialogControls,
    addBlockchainAddressChooserDialogControls,
    addBlockchainAddressNetworkDialogControls,
    addBlockchainAddressesDialogControls,
    createEmbeddedWalletDialogControls,
    inviteSignerDialogControls,
    removeSignerDialogControls,
    resetPasskeyDialogControls,
    signerEnrollDialogControls,
    smsRecoveryDialogControls,
  };

  return (
    <DialogsContext.Provider value={dialogControls}>
      {children}

      <BankAccountsAddFormDialog dialogControls={addBankAccountDialogControls} />

      <BlockchainAddressesAddFormDialog dialogControls={addBlockchainAddressesDialogControls} />

      <BlockchainAddressAddNetworkFormDialog dialogControls={addBlockchainAddressNetworkDialogControls} />

      <CreateEmbeddedWalletDialog dialogControls={createEmbeddedWalletDialogControls} />

      <SmsRecoveryDialog dialogControls={smsRecoveryDialogControls} />

      <ResetPasskeyDialog dialogControls={resetPasskeyDialogControls} />

      <InviteSignerDialog dialogControls={inviteSignerDialogControls} />

      <RemoveSignerDialog dialogControls={removeSignerDialogControls} />

      <SignerEnrollDialog dialogControls={signerEnrollDialogControls} />

      <AddBlockchainAddressChooserDialog
        agoraWalletControls={createEmbeddedWalletDialogControls}
        dialogControls={addBlockchainAddressChooserDialogControls}
        externalWalletControls={addBlockchainAddressesDialogControls}
      />
    </DialogsContext.Provider>
  );
}

export function useDialogControlsContext(): DialogsContextState {
  return use(DialogsContext);
}
